/**
 * Error Tracking Service
 * Équivalent de error_tracker.dart et error_service.dart dans Flutter
 */

import { analyticsService } from './analytics'

interface ErrorContext {
  [key: string]: any
}

class ErrorTracker {
  private static instance: ErrorTracker
  private initialized = false

  private constructor() {}

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker()
    }
    return ErrorTracker.instance
  }

  initialize() {
    if (this.initialized) return

    // Global error handler
    window.addEventListener('error', (event) => {
      this.captureError(event.error, {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    })

    // Promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        new Error(event.reason),
        {
          type: 'unhandledrejection',
          reason: event.reason,
        }
      )
    })

    this.initialized = true
    console.debug('🛡️ Error Tracker initialized')
  }

  captureError(error: Error, context?: ErrorContext) {
    try {
      console.error('🔴 Error captured:', {
        error,
        context,
        timestamp: new Date().toISOString(),
      })

      // Track error in analytics
      analyticsService.trackError(error, context?.context || 'unknown')

      // In production, you would send this to a service like Sentry
      if (import.meta.env.PROD) {
        // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
      }
    } catch (err) {
      console.error('Error while capturing error:', err)
    }
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext) {
    try {
      console.debug(`📝 Message captured [${level}]:`, {
        message,
        context,
        timestamp: new Date().toISOString(),
      })

      analyticsService.trackEvent('message_logged', {
        message,
        level,
        ...context,
      })
    } catch (err) {
      console.error('Error while capturing message:', err)
    }
  }

  setUser(userId: string, userInfo?: Record<string, any>) {
    try {
      analyticsService.setUser(userId)

      if (userInfo) {
        analyticsService.setUserProperty(userInfo)
      }

      console.debug('👤 User set in error tracker:', userId)
    } catch (err) {
      console.error('Error while setting user:', err)
    }
  }

  clearUser() {
    try {
      console.debug('🚪 User cleared from error tracker')
      // Clear user context
    } catch (err) {
      console.error('Error while clearing user:', err)
    }
  }
}

// Export singleton instance
export const errorTracker = ErrorTracker.getInstance()
